/** Admin: edit box score for a selected game. */
import { useEffect, useState, useMemo } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

const STAT_FIELDS = [
    ["minutes", "MIN"], ["points", "PTS"], ["rebounds", "REB"], ["assists", "AST"],
    ["steals", "STL"], ["blocks", "BLK"],
    ["fg_made", "FGM"], ["fg_attempts", "FGA"],
    ["three_made", "3PM"], ["three_attempts", "3PA"],
    ["ft_made", "FTM"], ["ft_attempts", "FTA"],
];

export default function AdminBoxScores() {
    const [games, setGames] = useState([]);
    const [players, setPlayers] = useState([]);
    const [gameId, setGameId] = useState("");
    const [lines, setLines] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/games").then((r) => setGames(r.data));
        api.get("/players?all=true").then((r) => setPlayers(r.data));
    }, []);

    useEffect(() => {
        if (!gameId) { setLines([]); return; }
        api.get(`/games/${gameId}/box-score`).then((r) => {
            // seed with current players if empty
            if (r.data.length === 0) {
                setLines(players.map((p) => ({
                    player_id: p.id, player_name: p.name, jersey_number: p.jersey_number,
                    game_id: gameId,
                    minutes: 0, points: 0, rebounds: 0, assists: 0, steals: 0, blocks: 0,
                    fg_made: 0, fg_attempts: 0, three_made: 0, three_attempts: 0, ft_made: 0, ft_attempts: 0,
                })));
            } else {
                setLines(r.data);
            }
        });
    }, [gameId, players]);

    const updateCell = (idx, key, value) => {
        setLines((arr) => arr.map((row, i) => i === idx ? { ...row, [key]: Number(value) || 0 } : row));
    };

    const save = async () => {
        if (!gameId) return;
        setSaving(true);
        try {
            const payload = lines.map((l) => ({
                game_id: gameId,
                player_id: l.player_id, player_name: l.player_name, jersey_number: l.jersey_number,
                minutes: l.minutes, points: l.points, rebounds: l.rebounds, assists: l.assists,
                steals: l.steals, blocks: l.blocks,
                fg_made: l.fg_made, fg_attempts: l.fg_attempts,
                three_made: l.three_made, three_attempts: l.three_attempts,
                ft_made: l.ft_made, ft_attempts: l.ft_attempts,
            }));
            await api.put(`/games/${gameId}/box-score`, payload);
            toast.success("Box score saved");
        } catch (err) { toast.error(formatErr(err)); }
        finally { setSaving(false); }
    };

    const finalGames = useMemo(() => games.filter((g) => g.status === "final"), [games]);

    return (
        <div data-testid="admin-box-scores">
            <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Stat Sheet</div>
            <h1 className="h-display text-4xl mb-8">Box Scores</h1>
            <div className="card-flat p-5 mb-6">
                <label className="text-[10px] tracking-widest uppercase text-ink font-bold">Select Game</label>
                <select value={gameId} onChange={(e) => setGameId(e.target.value)} data-testid="bx-game-select" className="w-full mt-2 px-4 py-3 text-sm max-w-lg">
                    <option value="">— Pick a final game —</option>
                    {finalGames.map((g) => (
                        <option key={g.id} value={g.id}>
                            {new Date(g.date).toLocaleDateString()} — vs. {g.opponent} ({g.result})
                        </option>
                    ))}
                </select>
            </div>

            {gameId && lines.length > 0 && (
                <>
                    <div className="card-flat overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-bg-soft border-b border-line">
                                <tr>
                                    <th className="text-left p-2 text-[10px] uppercase tracking-widest text-ink sticky left-0 bg-bg-soft">Player</th>
                                    {STAT_FIELDS.map(([k, l]) => (
                                        <th key={k} className="text-right p-2 text-[10px] uppercase tracking-widest text-ink">{l}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((row, i) => (
                                    <tr key={row.player_id} className="border-t border-line">
                                        <td className="p-2 sticky left-0 bg-card">
                                            <div className="text-white">{row.player_name}</div>
                                            <div className="text-ink text-[10px]">#{row.jersey_number}</div>
                                        </td>
                                        {STAT_FIELDS.map(([k]) => (
                                            <td key={k} className="p-1">
                                                <input
                                                    type="number"
                                                    value={row[k] ?? 0}
                                                    onChange={(e) => updateCell(i, k, e.target.value)}
                                                    data-testid={`bx-${row.player_id}-${k}`}
                                                    className="w-16 px-2 py-1 text-right text-sm"
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <button onClick={save} disabled={saving} data-testid="bx-save" className="btn-gold px-7 py-4 text-xs mt-5 disabled:opacity-60">
                        {saving ? "Saving…" : "Save Box Score"}
                    </button>
                </>
            )}
        </div>
    );
}
